import { useEffect, useRef, useMemo, useCallback, useState, memo } from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { BrandColors } from "@neumind/shared/branding";

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
}

interface AnimatedBackgroundProps {
  nodeCount?: number;
  connectionDistance?: number;
  speed?: number;
  opacity?: number;
}

// Detect if device prefers reduced motion
const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Detect mobile for reduced complexity
const isMobile =
  typeof window !== "undefined" && window.innerWidth < 768;

function AnimatedBackgroundComponent({
  nodeCount = isMobile ? 20 : 40, // Reduce nodes on mobile
  connectionDistance = isMobile ? 100 : 150, // Smaller connection distance on mobile
  speed = 0.3,
  opacity = 0.6,
}: AnimatedBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const nodesRef = useRef<Node[]>([]);
  const isVisibleRef = useRef(true);
  const [canvasSupported, setCanvasSupported] = useState(true);

  const colors = useMemo(
    () => [
      alpha(BrandColors.primary.main, 0.7),
      alpha(BrandColors.primary.light, 0.7),
      alpha(BrandColors.secondary.main, 0.7),
      alpha(BrandColors.secondary.light, 0.7),
    ],
    []
  );

  // Check canvas support
  useEffect(() => {
    const canvas = document.createElement("canvas");
    const supported = !!(canvas.getContext && canvas.getContext("2d"));
    setCanvasSupported(supported);
  }, []);

  // Memoized resize handler
  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    }
  }, []);

  // Memoized node initialization
  const initNodes = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    nodesRef.current = Array.from({ length: nodeCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * speed,
      vy: (Math.random() - 0.5) * speed,
      radius: Math.random() * 3 + 2,
      color: colors[Math.floor(Math.random() * colors.length)],
    }));
  }, [nodeCount, speed, colors]);

  useEffect(() => {
    // Skip animation if reduced motion is preferred
    if (prefersReducedMotion || !canvasSupported) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const drawNode = (node: Node) => {
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      ctx.fillStyle = node.color;
      ctx.fill();
    };

    const drawConnection = (node1: Node, node2: Node, distance: number) => {
      const lineOpacity = 1 - distance / connectionDistance;
      ctx.beginPath();
      ctx.moveTo(node1.x, node1.y);
      ctx.lineTo(node2.x, node2.y);
      ctx.strokeStyle = alpha(BrandColors.primary.main, lineOpacity * 0.2);
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    const updateNode = (node: Node) => {
      node.x += node.vx;
      node.y += node.vy;

      // Bounce off edges
      if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
      if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

      // Keep in bounds
      node.x = Math.max(0, Math.min(canvas.width, node.x));
      node.y = Math.max(0, Math.min(canvas.height, node.y));
    };

    const animate = () => {
      // Skip rendering if not visible
      if (!isVisibleRef.current) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const nodes = nodesRef.current;
      const connectionDistSq = connectionDistance * connectionDistance;

      // Draw connections - use squared distance to avoid sqrt
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distSq = dx * dx + dy * dy;

          if (distSq < connectionDistSq) {
            const distance = Math.sqrt(distSq);
            drawConnection(nodes[i], nodes[j], distance);
          }
        }
      }

      // Draw and update nodes
      for (const node of nodes) {
        drawNode(node);
        updateNode(node);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    resizeCanvas();
    initNodes();
    animate();

    // Use IntersectionObserver to pause when not visible
    const observer = new IntersectionObserver(
      (entries) => {
        isVisibleRef.current = entries[0]?.isIntersecting ?? true;
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    // Debounced resize handler
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeCanvas();
        initNodes();
      }, 100);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [nodeCount, connectionDistance, speed, colors, canvasSupported, resizeCanvas, initNodes]);

  // Static fallback for reduced motion or unsupported canvas
  const staticBackground = (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        opacity,
        background: `
          radial-gradient(circle at 18% 24%, ${alpha(BrandColors.primary.main, 0.08)}, transparent 60%),
          radial-gradient(circle at 82% 32%, ${alpha(BrandColors.secondary.main, 0.12)}, transparent 65%)
        `,
      }}
    />
  );

  if (prefersReducedMotion || !canvasSupported) {
    return staticBackground;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        opacity,
        background: `
          radial-gradient(circle at 18% 24%, ${alpha(BrandColors.primary.main, 0.08)}, transparent 60%),
          radial-gradient(circle at 82% 32%, ${alpha(BrandColors.secondary.main, 0.12)}, transparent 65%)
        `,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          willChange: "transform", // GPU acceleration hint
        }}
      />
    </Box>
  );
}

// Memoize the component to prevent unnecessary re-renders
export const AnimatedBackground = memo(AnimatedBackgroundComponent);
